// Tooltip copy for the "i" explainers shown on cards and KPI tiles.
// Each entry: { title, text, foot }. Brief, decision-relevant — not echo.

import type { InfoContent } from "../components/InfoButton";

export const I: Record<string, InfoContent> = {
  // ---- Executive ----
  K001: {
    title: "K-001 · Aggregate over/underbilling",
    text:
      "Net portfolio position from cost-to-cost percent-complete. Positive = over-billed (cash in advance of work earned); negative = under-billed (working capital lent to customers).",
    foot: "Source: proj_invoice_jour + proj_journal_trans · vintage v2026.18",
  },
  K023: {
    title: "K-023 · Backlog (uncompleted contract value)",
    text:
      "Contract value remaining to be earned across all active projects (current contract − earned-to-date). The horizon of revenue we still have to convert.",
    foot: "Source: proj_table · billing_summary",
  },
  K013: {
    title: "K-013 · Enterprise DSO",
    text:
      "Days sales outstanding, weighted across active customers. Drilled by tier (direct / first-tier / sub-to-sub) — sub-to-sub structurally runs longer.",
    foot: "Source: cust_trans (last 90d) · payment_terms",
  },
  K003: {
    title: "K-003 · Mix shift index",
    text:
      "Share of new awards by project archetype, quarterly. Detects when the portfolio drifts away from a high-margin / favorable-billing archetype (e.g. clean-room).",
    foot: "Source: proj_table.planned_start_date by project_type_code",
  },
  K022: {
    title: "K-022 · AR balance",
    text:
      "Open trade receivables across all customers at the snapshot date. Excludes retainage withheld (K-008) and unbilled work-in-process.",
    foot: "Source: cust_trans · open invoices",
  },
  K024: {
    title: "K-024 · Overdue >90d",
    text:
      "Receivables aged past 90 days. The construction tail (91-120, 120+) is the early signal of customer distress — typically precedes a write-off by 30-60 days.",
    foot: "Source: cust_trans aging buckets",
  },
  K028: {
    title: "K-028 · Customer composite risk",
    text:
      "Per-customer 0-100 score combining pay-cycle drift, Paydex baseline, and external distress signals (liens, judgments, news). ≥50 = watchlist; ≥75 = critical.",
    foot: "Inputs: cust_trans · D&B Paydex · B4 lien feed · PACER",
  },
  K002a: {
    title: "K-002 · Total over-billed",
    text:
      "Sum of positive over/underbilling positions across projects. High over-billing supports cash today but depresses future periods as work is earned.",
    foot: "Source: billing_summary · over_under_billed > 0",
  },
  K002b: {
    title: "K-002 · Total under-billed",
    text:
      "Sum of negative positions — work earned but not yet billed. Working capital lent to customers; collected as pay applications catch up.",
    foot: "Source: billing_summary · over_under_billed < 0",
  },
  K008: {
    title: "K-008 · Retainage withheld",
    text:
      "Cumulative retainage held by customers (typically 5-10% per pay app). Recovered on substantial completion / project close-out — not before.",
    foot: "Source: proj_invoice_jour.retain_amount",
  },
  K009b: {
    title: "K-009b · Net Income Forecast",
    text:
      "Portfolio-level rolling forecast. Hierarchical Bayesian with conformal prediction intervals (MAPIE). Runs parallel to D365 bottom-up and reports variance.",
    foot: "Target: ±5% annual, <10% quarterly miss · 80% PI coverage ≥80%",
  },
  K040: {
    title: "K-040 · Prediction interval coverage",
    text:
      "Empirical coverage of the model's prediction intervals on rolling holdouts. The contract: 80% PI should contain the actual ≥80% of the time. Conformal calibration enforces this.",
    foot: "Method: MAPIE jackknife+ · 8-quarter holdout window",
  },
  KAR: {
    title: "AR drift surface",
    text:
      "Pay-cycle days behavior by tier. Direct-to-owner clears fastest; sub-to-sub structurally lags 40-60 days. Drift within a tier is the early-warning signal.",
    foot: "Source: cust_trans · pay_cycle_days · trailing 12m",
  },
  K015: {
    title: "K-015 · Cash-Negative Probability (90d)",
    text:
      "Probability that the operating cash position falls below the covenant floor + buffer in the next 90 days. Computed from the Net Income Forecast distribution and the scenario simulator.",
    foot: "Threshold: > 5% → executive briefing · > 10% → board notification",
  },
  K014: {
    title: "K-014 · DSO Drift Enterprise",
    text:
      "Rolling-30-day DSO minus the trailing-12-month enterprise baseline. The single best leading indicator for AR & Customer Risk — surfaces 30-45 days before AR aging shows it.",
    foot: "Threshold: > +5d for two consecutive weeks → alert",
  },
  K018: {
    title: "K-018 · Top-10 Customer AR Concentration",
    text:
      "Σ(top-10 customers' AR balance) / total AR. High concentration amplifies single-customer distress events into enterprise-level cash issues.",
    foot: "Threshold: > 60% → review · > 75% → escalate",
  },
  K021: {
    title: "K-021 · Gross Margin (Enterprise · TTM)",
    text:
      "Σ(revenue − cost) / Σ(revenue), trailing twelve months. Mix-shift effects appear here as a lagging signal — pair with K-022 for early detection.",
    foot: "Primary diagnostic for the Net Income Forecast model",
  },
  K014c: {
    title: "K-014c · Net Cash Position",
    text:
      "Cash + cash equivalents − short-term debt. The number that matters before any forecast — the actual liquidity available to meet obligations now.",
    foot: "Sourced from GL postings reconciled to D365 cash-account totals daily",
  },
  K024Cov: {
    title: "K-024 · DSCR (Debt-Service Coverage)",
    text:
      "EBITDA / debt service (interest + scheduled principal). Direct covenant-binding metric — the headline figure for any banking-package conversation.",
    foot: "Covenant: > 1.50× · breach risk: < 1.25×",
  },
  K002: {
    title: "K-002 · 8-Quarter Over/Underbilling Forecast",
    text:
      "Quarterly point forecast for K-001 with conformal 50/80/95 prediction intervals. Replays must reproduce the historical clean-room shift to be considered acceptable.",
    foot: "Target: ±10% point · ≥80% PI coverage on holdout",
  },
  K006: {
    title: "K-006 · Underbilling % of Revenue",
    text:
      "max(0, −K-001) / trailing-12 revenue. Quantifies the working-capital lent to customers as a share of business volume.",
    foot: "Threshold: > 6% → flag",
  },
  K016: {
    title: "K-016 · AR Aging (>90d bucket)",
    text:
      "Standard aging — final-stage AR risk visibility. The 91-120 and 120+ tail is the leading indicator of write-off risk. A customer that hits 120+ has roughly 3× the historical write-off rate.",
    foot: "Threshold: > 8% for two weeks → alert",
  },
  K028Crit: {
    title: "K-028 · Customers in Critical Tier",
    text:
      "Customers with composite distress score ≥ 75. Each one warrants action: tighten retainage, request joint checks, or escalate to the credit committee.",
    foot: "Movement up a tier → notification · entry into Critical → executive review",
  },
  K009c: {
    title: "K-009c · Quarterly Forecast Miss vs D365",
    text:
      "|forecast − actual| / |actual| at the layer's stated horizon, on rolling holdouts. D365 baseline tends to run ~20% miss; the AI model targets <10%.",
    foot: "Target: < 10% quarterly miss",
  },
  K022b: {
    title: "K-022b · Gross Margin Shrinkage QoQ",
    text:
      "GM_q − GM_(q−1) at enterprise level and per project type. The change-of-change view; where the mix-shift effect is most visible.",
    foot: "Threshold: < −150bp → flag",
  },
  K014b: {
    title: "K-014b · Operating Cash Flow",
    text:
      "Cash generated by operations on a trailing-12 basis. Net income plus non-cash items plus / minus working-capital changes (AR, AP, retainage, WIP). The cash-quality counterpart to gross margin.",
    foot: "Source: GeneralJournalAccountEntry + working-capital deltas · vintage v2026.18",
  },
  l1Materials: {
    title: "Material commodity spike monitor",
    text:
      "Z-score of latest commodity print against trailing-12 baseline. Cement / steel / lumber / copper are the four highest-impact inputs; spike (>2σ) triggers pricing-clause review.",
    foot: "Sources: BLS PPI series · K-103 / K-104 / K-105 / K-106",
  },
  l3MacroBand: {
    title: "Macro context · forecast covariates",
    text:
      "External signals fed to the Net Income Forecast model. ABI leads construction billings 9-12 months; PPI tracks input-cost shocks; Treasury anchors discount assumptions.",
    foot: "Sources: AIA · BLS · FRED · Dodge",
  },
  l2PayCycle: {
    title: "Pay-cycle distribution by tier",
    text:
      "Boxplots of customer pay-cycle days, by tier. Each box is Q1-Q3, whiskers are min-max, dotted line is the trailing baseline median. Drift of the median above the baseline is the early signal.",
    foot: "Source: cust_trans · trailing 90d window",
  },
  c360Signals: {
    title: "External signal scoreboard",
    text:
      "Per-customer breakdown of the K-028 inputs. Each tile is one external signal source with current value and a flag (OK / WATCH / BAD) based on its threshold.",
  },
  cpiSpi: {
    title: "Project performance · CPI vs SPI",
    text:
      "Earned-value snapshot of every active project. SPI on x (actual % complete ÷ scheduled % complete) — left of 1 = behind schedule. CPI on y (original EAC ÷ current EAC) — below 1 = over budget. Bottom-left quadrant = behind AND over budget; that's the escalation zone. Concentric dashed ellipses are the ±1σ (in-control) and ±2σ (watchlist) envelopes. Dot size = contract value.",
    foot: "Sources: proj_table (planned dates, eac_drift_pct) · billing_summary (last_pct_complete) · σ = 0.10",
  },
  cpiSpiSummary: {
    title: "Portfolio earned-value summary",
    text:
      "Contract-value-weighted aggregate CPI and SPI across all active projects, with the count of projects in each control band (in-control / watchlist / escalate). Use the outlier list below to drill into the projects driving any aggregate movement.",
  },
  l2DsoDrift: {
    title: "DSO drift signal — 16 weeks",
    text:
      "Each dot is the weekly drift of enterprise DSO vs the trailing-12 baseline. Above the dashed zero line = behind baseline. The K-014 alert threshold is +5d sustained for 2+ consecutive weeks; production wires this into the on-call rotation.",
    foot: "Source: cust_trans · pay_cycle_days × invoice value · weekly snapshot",
  },
  simRepriced: {
    title: "Scenario re-priced KPIs",
    text:
      "The four most decision-relevant portfolio KPIs re-priced under the scenario inputs on the left. Net cash impact and NI delta cascade from the underlying lever sensitivities (clean-room mix, DSO, retainage timing, CO lag, awards growth, PPI overlay). Severity band on the foot reflects the % of baseline NI moved.",
    foot: "Same model as Layer-3 forecast · scenario applied to current vintage",
  },
  Kcash: {
    title: "K-cash · scenario net cash impact",
    text:
      "Sum of all lever contributions: mix shift, DSO shock, retainage release, CO lag, awards growth, PPI overlay. Positive = cash freed. Negative impact below -$1M triggers the executive-briefing threshold.",
  },
  K009bScn: {
    title: "K-009b · NI horizon delta under scenario",
    text:
      "Net-income delta over the forecast horizon implied by the scenario. Approximately 0.62× of net cash impact (the historical pass-through rate from cash to NI for this portfolio).",
  },
  c360Cos: {
    title: "Change order history",
    text:
      "Submitted change orders for this customer's projects, with approval lag (days from submitted to approved) and the dollar delta to revenue. Persistent CO submission with long approval lag is a leading indicator of cash-cycle drift on the customer.",
    foot: "Source: proj_change_order · scope reasons + approval timing",
  },
  l3Macro: {
    title: "Macro covariates feeding the forecast",
    text:
      "External signals the model uses as covariates: ABI (architecture billings, leading indicator 9-12m), PPI cement (input cost shock proxy), 10Y treasury (discount-rate driver), construction PPI (broad input inflation), backlog ratio (revenue runway). Strong covariates are listed; weights live in the model card.",
    foot: "Sources: AIA · BLS · FRED · internal backlog · model card v2026.18",
  },
  simCustForm: {
    title: "Customer attributes — input panel",
    text:
      "11 editable inputs map to the live composite-risk score and the four predicted KPIs on the right. Tier and Paydex carry the highest weights (1.5-1.6); pay-if-paid clauses (1.4) and contract type (1.0) are next. The full weight schema is in lib/scoring.ts and is exposed in the production audit pack.",
    foot: "Same scorecard as the production model · transparent weighted-additive form",
  },

  // ---- Cards ----
  execFan: {
    title: "Net income forecast — 24m rolling",
    text:
      "Solid line: actuals. Dashed: AI forecast. Dotted: latest D365 bottom-up. Bands: 50/80/95% prediction intervals (conformal-calibrated). NOW marker = snapshot date.",
    foot: "Drill into any month to see SHAP driver decomposition",
  },
  execMix: {
    title: "Portfolio mix shift",
    text:
      "Distribution: stacked share by archetype each quarter. Divergence: pp change vs a baseline quarter (picker). Hero series (largest mover) is emphasized.",
    foot: "Click a band to filter Portfolio Billing to that archetype",
  },
  execAlerts: {
    title: "Critical KRI breaches",
    text:
      "Active customer-level distress events from external feeds in the last 90 days, ranked by severity. Each row drills to Customer 360.",
    foot: "Sources: D&B Paydex · B4 mechanic's-lien feed · PACER",
  },
  execMacro: {
    title: "Macro & industry context",
    text:
      "External covariates fed to the forecast model. ABI is a leading indicator of construction billings; PPI tracks input-cost spikes; Treasury curve drives discount assumptions.",
    foot: "Sources: AIA · BLS · FRED",
  },
  l1Fan: {
    title: "Aggregate over/underbilling — 8Q forecast",
    text:
      "Net position trajectory with conformal PI. Zero-baseline shown. The replay test enforces that the model would have detected the historical clean-room shift.",
    foot: "Backtested coverage 82.4% on 80% PI · target ≥80%",
  },
  l1Peer: {
    title: "Peer-deviation scatter",
    text:
      "% billed vs % complete per project (cost-to-cost). Diagonal = peer median; ±1.5σ band shown. Points above the band = over-billed vs archetype peers; below = under-billed.",
    foot: "Click any point to drill to the project record",
  },
  l1Flags: {
    title: "Project exception list",
    text:
      "Projects flagged for: large over-billing (>$500K), under-billing (<-$50K), peer deviation (>1.5σ from archetype median), or stale EAC (no progress on a >$500K contract).",
    foot: "Filters cumulate left-to-right; default: all flags",
  },
  l1Msi: {
    title: "Mix Shift Index (rolling)",
    text:
      "Rolling stdev of clean-room share. Threshold 0.10 = portfolio composition is moving fast enough to materially change the over-billing trajectory.",
    foot: "Computed from K-003 quarterly shares · 3-quarter window",
  },
  l2Dso: {
    title: "DSO with tier decomposition",
    text:
      "Stacked area shows enterprise DSO and tier mix over 12 months. Sub-to-sub layer thickening is the cleanest sign that downstream cash is slowing.",
    foot: "Computed from cust_trans · pay_cycle_days × invoice value",
  },
  l2Aging: {
    title: "AR aging buckets",
    text:
      "Standard 30-day buckets. The 91-120 and 120+ tail is the leading indicator — a customer that hits 120+ has a ~3× write-off risk vs current AR.",
    foot: "As-of snapshot; rebuilt nightly from cust_trans",
  },
  l2Watch: {
    title: "Customer Risk Watchlist",
    text:
      "Composite K-028 score per customer. Combines pay-cycle drift, Paydex baseline, and external distress events. Sortable; clicks drill to Customer 360.",
    foot: "Score threshold: 50 = watchlist · 75 = critical",
  },
  l2Events: {
    title: "External distress events",
    text:
      "D&B Paydex updates, mechanic's-lien filings, civil judgments, and labeled distress flags from the last 90 days, ranked by severity.",
    foot: "Sources: D&B · B4 · PACER · internal labels",
  },
  c360Projects: {
    title: "Active projects with this customer",
    text:
      "Per-project contract / earned / billed / over-under. Highlights any project where billed deviates from earned by >5pp.",
  },
  c360Pay: {
    title: "Pay-cycle history · 12m",
    text:
      "Days from invoice to payment, monthly. Baseline = first-6-month median. Drift above baseline is the earliest behavioral signal of customer distress.",
  },
  c360Events: {
    title: "Distress timeline · 90d",
    text:
      "External signals filtered to this customer. Severity coding follows the K-028 weighting scheme.",
  },
  c360Actions: {
    title: "Recommended actions",
    text:
      "Rules-based: pay-cycle >90d → tighten retainage; |WIP delta| >5% → reconcile EAC; high-severity event in 90d → credit committee.",
    foot: "Layer-4 simulator scores each action's expected NPV impact",
  },
  l3Fan: {
    title: "Forecast trajectory vs actuals & D365",
    text:
      "Same model as the Overview tile. The D365 overlay is the bottom-up forecast from project managers — the variance vs AI is itself a KRI.",
    foot: "Conformal PI · vintage v2026.18",
  },
  l3Drivers: {
    title: "Driver decomposition vs prior vintage",
    text:
      "Top 6 features moving the current forecast vs the prior vintage. Aggregated SHAP from the underlying gradient-boosted model.",
    foot: "Bars sorted by absolute impact",
  },
  l3Vintage: {
    title: "Vintage stability",
    text:
      "Same target quarter forecast across the last 6 vintages. A flat trajectory = stable model; a drifting trajectory = the model is learning new signal each cycle.",
  },
  simShap: {
    title: "SHAP — top drivers for this prediction",
    text:
      "Each bar is one feature's marginal contribution to the predicted score for this customer. Right (red) = increases default risk; left (green) = decreases.",
    foot: "Method: TreeSHAP on the GBM · per-prediction local explanation",
  },
  simPeer: {
    title: "Peer cohort positioning",
    text:
      "Where this customer sits vs same-tier same-NAICS peers on pay-cycle, Paydex, and distress incidence. Cohort built from internal AR + external bureau data.",
  },
  simSliders: {
    title: "Portfolio scenario inputs",
    text:
      "Move sliders to re-price the portfolio under a stress scenario. Cash impact computed against the latest baseline forecast — the mix-shift sensitivity is shown in the tornado chart.",
  },
  simWaterfall: {
    title: "Cash impact waterfall · scenario vs baseline",
    text:
      "Shows the contribution of each scenario lever to the change in 12-month NI. Read left-to-right; ending bar is the scenario NI.",
  },
  simTornado: {
    title: "Sensitivity tornado",
    text:
      "Single-lever sensitivity at ±1 unit. Identifies which scenario dimensions have the largest first-order impact on cash.",
  },
  dpCash: {
    title: "Operating cash trajectory · 24m",
    text:
      "Banking-grade view of operating cash with PI bands. Min-cash and trough quarter are the covenant inputs the bank will care about.",
    foot: "Reconciles to GL · vintage v2026.18",
  },
  dpBank: {
    title: "Bank covenants & coverage",
    text:
      "Live state of each covenant with target threshold. Status: PASS / WATCH (within 10% of breach) / BREACH.",
  },
  dpEsop: {
    title: "ESOP valuation feeder",
    text:
      "Inputs to the repurchase-obligation model. EBITDA TTM and backlog conversion drive the DCF; debt-free cash and WACC anchor the discount.",
    foot: "Updated quarterly · linked to plan-year valuation cycle",
  },
  dpAudit: {
    title: "Audit trail",
    text:
      "Lineage of every figure in this pack — from D365 source tables through to model artifacts. Every number drills to its source row.",
    foot: "Export bundles include full lineage JSON",
  },
};
